import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth/auth-provider";
import { Button, HenosisWordmark } from "@/components/ui/primitives";

const DEMO_EMAIL = "demo@argus-dev.local";
const DEMO_PASSWORD = "argus-demo-2026!";

export function SignInPage() {
  const { signInWithPassword, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) void navigate({ to: "/select-tenant", replace: true });
  }, [user, navigate]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const { error: err } = await signInWithPassword(email, password);
    if (err) {
      setError(err);
      setSubmitting(false);
      return;
    }
    await navigate({ to: "/select-tenant", replace: true });
  }

  return (
    <main className="argus-glow bg-neutral-975 flex min-h-dvh items-center justify-center px-6 text-neutral-100">
      <div className="w-full max-w-sm space-y-7">
        <header className="flex flex-col items-center space-y-3 text-center">
          <HenosisWordmark className="h-12" />
          <div className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight">Argus</h1>
            <p className="text-sm text-neutral-400">
              The AI compliance platform for private fund GPs.
            </p>
          </div>
        </header>

        <form
          onSubmit={(e) => void onSubmit(e)}
          className="space-y-4 rounded-xl border border-neutral-800/80 bg-neutral-900/40 p-6 shadow-xl shadow-black/30"
        >
          <div className="space-y-1.5">
            <label
              htmlFor="email"
              className="text-[11px] font-medium uppercase tracking-wide text-neutral-500"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="password"
              className="text-[11px] font-medium uppercase tracking-wide text-neutral-500"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
            />
          </div>

          {error && (
            <div className="rounded-md border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          )}

          <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </Button>

          <button
            type="button"
            onClick={() => {
              setEmail(DEMO_EMAIL);
              setPassword(DEMO_PASSWORD);
            }}
            className="w-full text-center text-[11px] text-neutral-500 transition hover:text-neutral-300"
          >
            Use demo credentials
          </button>
        </form>

        <p className="text-center text-xs text-neutral-500">
          New here?{" "}
          <Link to="/sign-up" className="text-neutral-300 underline-offset-2 hover:underline">
            Create an account
          </Link>
        </p>
      </div>
    </main>
  );
}
